import React, { useState } from 'react';
import { Contact } from '../types';
import { formatPhone } from '../utils/phoneUtils';
import { useTheme } from '../contexts/ThemeContext';

interface ContactListProps {
  contacts: Contact[];
  loading: boolean;
  onEdit: (contact: Contact) => void;
  onDelete: (id: string) => void;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onBulkDelete: (ids: string[]) => void;
}

export function ContactList({ contacts, loading, onEdit, onDelete, selectedIds, onSelectionChange, onBulkDelete }: ContactListProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  // Edit button: cores diferentes para dark/light para garantir contraste
  const editBg = isDark ? 'rgba(105, 110, 119, 0.1)' : 'rgba(139, 141, 152, 0.1)';
  const editBgHover = isDark ? 'rgba(105, 110, 119, 0.2)' : 'rgba(139, 141, 152, 0.2)';
  const editIconColor = isDark ? '#EDEEF0' : '#1C2024'; // Branco no dark, escuro no light
  // Delete button: mesma cor em ambos os temas (rosa claro funciona bem)
  const deleteBg = 'rgba(229, 70, 102, 0.15)';
  const deleteBgHover = 'rgba(229, 70, 102, 0.2)';
  const deleteIconColor = '#FF949D'; // Rosa claro funciona bem em ambos
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<string | null>(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Selection logic
  const allOnPageSelected = contacts.length > 0 && contacts.every(c => selectedIds.has(c.id));
  const someOnPageSelected = contacts.some(c => selectedIds.has(c.id));

  const handleSelectAll = () => {
    const next = new Set(selectedIds);
    if (allOnPageSelected) {
      // Deselect all on this page
      contacts.forEach(c => next.delete(c.id));
    } else {
      // Select all on this page
      contacts.forEach(c => next.add(c.id));
    }
    onSelectionChange(next);
  };

  const handleSelectOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  };

  const handleDeleteClick = (id: string) => {
    setContactToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (contactToDelete) {
      onDelete(contactToDelete);
    }
    setShowDeleteConfirm(false);
    setContactToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setContactToDelete(null);
  };

  const handleBulkDeleteClick = () => {
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = () => {
    onBulkDelete(Array.from(selectedIds));
    setShowBulkDeleteConfirm(false);
  };

  const cancelBulkDelete = () => {
    setShowBulkDeleteConfirm(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-label="Carregando contatos">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: 'var(--astra-dark-blue)' }}></div>
        <span className="ml-2 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>Carregando...</span>
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>
        <p className="text-sm font-normal">Nenhum contato encontrado.</p>
      </div>
    );
  }

  // Checkbox style
  const checkboxStyle: React.CSSProperties = {
    width: '16px',
    height: '16px',
    accentColor: 'var(--astra-dark-blue)',
    cursor: 'pointer',
  };

  return (
    <>
      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center justify-between px-4 py-2.5 rounded-lg mb-3 transition-all"
          style={{
            backgroundColor: isDark ? 'rgba(220, 38, 38, 0.12)' : 'rgba(220, 38, 38, 0.08)',
            border: isDark ? '1px solid rgba(220, 38, 38, 0.25)' : '1px solid rgba(220, 38, 38, 0.2)',
          }}
        >
          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            <strong>{selectedIds.size}</strong> contato{selectedIds.size > 1 ? 's' : ''} selecionado{selectedIds.size > 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onSelectionChange(new Set())}
              className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
              style={{
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6',
                color: 'var(--text-primary)',
              }}
            >
              Limpar seleção
            </button>
            <button
              onClick={handleBulkDeleteClick}
              className="px-3 py-1.5 rounded-md text-white text-xs font-medium transition-colors"
              style={{ backgroundColor: '#dc2626' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#b91c1c')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#dc2626')}
            >
              Excluir selecionados
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl" style={{
        border: isDark
          ? '1px solid rgba(255, 255, 255, 0.08)'
          : '1px solid rgba(15, 23, 42, 0.12)',
        backgroundColor: 'var(--bg-card)',
        overflow: 'hidden'
      }}>
        <table className="min-w-full divide-y divide-[var(--border-color-soft)] transition-colors" role="table" style={{ 
          backgroundColor: 'var(--bg-card)'
        }}>
          <thead
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderBottom: isDark
                ? '1px solid rgba(255, 255, 255, 0.08)'
                : '1px solid rgba(15, 23, 42, 0.12)'
            }}
          >
            <tr>
              <th className="px-4 py-3 text-center" style={{ width: '40px', borderTopLeftRadius: '12px' }}>
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
                  }}
                  onChange={handleSelectAll}
                  style={checkboxStyle}
                  aria-label="Selecionar todos os contatos da página"
                />
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>
                Nome
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>
                Telefone
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>
                Email
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>
                Tags
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>
                Categoria
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>
                Atualizado em
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold" style={{ 
                color: 'var(--text-muted)',
                borderTopRightRadius: '12px'
              }}>
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-color-soft)] transition-colors" style={{ 
            backgroundColor: 'var(--bg-card)'
          }}>
            {contacts.map((contact, index) => {
              const isFirstRow = index === 0;
              const isLastRow = index === contacts.length - 1;
              const isSelected = selectedIds.has(contact.id);
              
              return (
              <tr key={contact.id} className="transition-colors hover:bg-opacity-50" style={{
                '--hover-bg': 'var(--bg-secondary)',
                backgroundColor: isSelected
                  ? (isDark ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.05)')
                  : 'transparent'
              } as React.CSSProperties}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isSelected
                  ? (isDark ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.05)')
                  : 'transparent';
              }}>
                <td className="px-4 py-4 text-center" style={{
                  ...(isFirstRow && { borderTopLeftRadius: '12px' }),
                  ...(isLastRow && { borderBottomLeftRadius: '12px' })
                }}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleSelectOne(contact.id)}
                    style={checkboxStyle}
                    aria-label={`Selecionar contato ${contact.nome}`}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-normal" style={{ 
                  color: 'var(--text-primary)',
                }}>
                  {contact.nome}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                  {formatPhone(contact.telefone)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                  {contact.email || '-'}
                </td>
                <td className="px-6 py-4 text-sm">
                  {contact.tags && contact.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {contact.tags.map((tag, index) => (
                        <span 
                          key={index} 
                          className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                          style={{ 
                            backgroundColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : '#EFF6FF',
                            color: theme === 'dark' ? '#93C5FD' : '#1E40AF',
                            border: theme === 'dark' ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid #BFDBFE'
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                  {contact.categoria ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-normal" style={{ 
                      color: theme === 'dark' ? '#ffffff' : 'var(--astra-dark-blue)', 
                      backgroundColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 58, 95, 0.1)' 
                    }}>
                      {contact.categoria.nome}
                    </span>
                  ) : (
                    <span className="font-normal" style={{ color: 'var(--text-muted)' }}>-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                  {formatDate(contact.atualizadoEm)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{
                  ...(isFirstRow && { borderTopRightRadius: '12px' }),
                  ...(isLastRow && { borderBottomRightRadius: '12px' })
                }}>
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => onEdit(contact)}
                      className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                      title="Editar"
                      aria-label={`Editar contato ${contact.nome}`}
                      style={{
                        background: editBg,
                        color: editIconColor
                      }}
                      onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.currentTarget.style.background = editBgHover;
                      }}
                      onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.currentTarget.style.background = editBg;
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                        aria-hidden="true"
                      >
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteClick(contact.id)}
                      className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                      title="Excluir"
                      aria-label={`Excluir contato ${contact.nome}`}
                      style={{
                        background: deleteBg,
                        color: deleteIconColor
                      }}
                      onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.currentTarget.style.background = deleteBgHover;
                      }}
                      onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.currentTarget.style.background = deleteBg;
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                        aria-hidden="true"
                      >
                        <path d="M3 6h18" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal de Confirmação de Exclusão Individual */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="rounded-lg shadow-xl max-w-md w-full mx-4" style={{
            background: theme === 'dark' ? 'linear-gradient(135deg, #1e1e1f 0%, #252527 100%)' : '#ffffff'
          }}>
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-4 text-n-slate-12">
                Confirmar Exclusão
              </h3>
              <p className="mb-6 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                Tem certeza que deseja excluir este contato?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{
                    backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all"
                  style={{
                    backgroundColor: '#dc2626'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#b91c1c')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#dc2626')}
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão em Massa */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="rounded-lg shadow-xl max-w-md w-full mx-4" style={{
            background: theme === 'dark' ? 'linear-gradient(135deg, #1e1e1f 0%, #252527 100%)' : '#ffffff'
          }}>
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-4 text-n-slate-12">
                Confirmar Exclusão em Massa
              </h3>
              <p className="mb-6 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                Tem certeza que deseja excluir <strong>{selectedIds.size}</strong> contato{selectedIds.size > 1 ? 's' : ''}? Esta ação não pode ser desfeita.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelBulkDelete}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{
                    backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmBulkDelete}
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all"
                  style={{
                    backgroundColor: '#dc2626'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#b91c1c')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#dc2626')}
                >
                  Excluir {selectedIds.size} contato{selectedIds.size > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}